import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

type LoadingScreenProps = {
  /** Called once when the intro reaches the exit phase (so the app can fade in behind it). */
  onComplete?: () => void;
};

const LoadingScreen = ({ onComplete }: LoadingScreenProps) => {
  const [phase, setPhase] = useState<'intro' | 'reveal' | 'tagline' | 'complete'>('intro');
  const [isVisible, setIsVisible] = useState(true);
  const onCompleteRef = useRef(onComplete);
  const didCompleteRef = useRef(false);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Elegant floating particles - fewer, larger, more refined
  const particles = useMemo(() => {
    return Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 2,
      delay: Math.random() * 0.8,
    }));
  }, []);

  useEffect(() => {
    const sequence = async () => {
      // Phase 1: Intro with particles (0-1000ms)
      await new Promise(resolve => setTimeout(resolve, 600));
      setPhase('reveal');
      
      // Phase 2: Brand reveal (1000-2200ms)
      await new Promise(resolve => setTimeout(resolve, 1400));
      setPhase('tagline');
      
      // Phase 3: Tagline + hold (2200-3400ms)
      await new Promise(resolve => setTimeout(resolve, 1200));
      setPhase('complete');

      // Signal app content to start fading in behind the loader
      if (!didCompleteRef.current) {
        didCompleteRef.current = true;
        onCompleteRef.current?.();
      }
      
      // Exit (3400-4000ms)
      await new Promise(resolve => setTimeout(resolve, 600));
      setIsVisible(false);
    };

    sequence();
  }, []);

  const brandName = "cloudmins";

  // Smooth spring config for organic feel
  const smoothSpring = {
    type: "spring" as const,
    stiffness: 100,
    damping: 20,
    mass: 1,
  };

  const letterAnimation = {
    hidden: { 
      opacity: 0, 
      y: 40,
      scale: 0.8,
      filter: "blur(12px)",
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      filter: "blur(0px)",
      transition: {
        delay: i * 0.06,
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94] as const, // easeOutQuad
      },
    }),
  };

  const taglineWords = ["Drones", "Data", "Dominance"];

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ 
            opacity: 0,
            scale: 1.02,
            filter: "blur(8px)",
          }}
          transition={{ 
            duration: 0.6, 
            ease: [0.4, 0, 0.2, 1] as const,
          }}
          className="fixed inset-0 z-[100] bg-background overflow-hidden"
        >
          {/* Ambient background - subtle, elegant */}
          <div className="absolute inset-0">
            {/* Primary glow - breathes slowly */}
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] sm:w-[900px] sm:h-[900px] rounded-full opacity-0"
              style={{
                background: "radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 70%)",
              }}
              animate={{
                opacity: [0, 0.6, 0.4],
                scale: [0.8, 1.1, 1],
              }}
              transition={{
                duration: 3,
                ease: "easeOut",
              }}
            />
            
            {/* Secondary glow - offset timing */}
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] sm:w-[700px] sm:h-[700px] rounded-full opacity-0"
              style={{
                background: "radial-gradient(circle, hsl(var(--secondary) / 0.08) 0%, transparent 60%)",
              }}
              animate={{
                opacity: [0, 0.4, 0.3],
                scale: [0.9, 1.15, 1.05],
              }}
              transition={{
                duration: 3.5,
                delay: 0.3,
                ease: "easeOut",
              }}
            />
          </div>

          {/* Floating particles - elegant drift towards center */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {particles.map((particle) => (
              <motion.div
                key={particle.id}
                className="absolute rounded-full"
                style={{
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                  width: particle.size,
                  height: particle.size,
                  background: `radial-gradient(circle, hsl(var(--primary) / 0.8) 0%, hsl(var(--primary) / 0.2) 100%)`,
                }}
                initial={{ 
                  opacity: 0,
                  scale: 0,
                }}
                animate={{
                  opacity: phase === 'complete' ? 0 : [0, 0.8, 0.6],
                  scale: phase === 'complete' ? 0 : [0, 1.2, 1],
                  x: phase === 'complete' 
                    ? `${(50 - particle.x) * 2}vw`
                    : `${(50 - particle.x) * 0.4}vw`,
                  y: phase === 'complete'
                    ? `${(50 - particle.y) * 2}vh`
                    : `${(50 - particle.y) * 0.4}vh`,
                }}
                transition={{
                  duration: phase === 'complete' ? 0.8 : 2.5,
                  delay: particle.delay,
                  ease: phase === 'complete' ? "easeIn" : "easeOut",
                }}
              />
            ))}
          </div>

          {/* Subtle grid overlay */}
          <motion.div
            className="absolute inset-0 opacity-0"
            style={{
              backgroundImage: `
                linear-gradient(hsl(var(--primary) / 0.03) 1px, transparent 1px),
                linear-gradient(90deg, hsl(var(--primary) / 0.03) 1px, transparent 1px)
              `,
              backgroundSize: "60px 60px",
            }}
            animate={{ opacity: phase !== 'intro' ? 0.5 : 0 }}
            transition={{ duration: 1.5 }}
          />

          {/* Main content container */}
          <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6">
            
            {/* Brand name */}
            <div className="relative">
              {/* Glow behind text */}
              <motion.div
                className="absolute inset-0 blur-[60px] -z-10 scale-150"
                initial={{ opacity: 0 }}
                animate={{ 
                  opacity: phase !== 'intro' ? 0.5 : 0,
                }}
                transition={{ duration: 1.2, delay: 0.3 }}
                style={{
                  background: "linear-gradient(135deg, hsl(var(--primary) / 0.4) 0%, hsl(var(--secondary) / 0.3) 100%)",
                }}
              />
              
              <motion.div 
                className="flex items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {brandName.split('').map((letter, i) => (
                  <motion.span
                    key={i}
                    custom={i}
                    variants={letterAnimation}
                    initial="hidden"
                    animate={phase !== 'intro' ? "visible" : "hidden"}
                    className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold inline-block"
                    style={{
                      background: "linear-gradient(135deg, hsl(var(--foreground)) 0%, hsl(var(--foreground) / 0.8) 50%, hsl(var(--primary)) 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundSize: "200% 200%",
                    }}
                  >
                    {letter}
                  </motion.span>
                ))}
              </motion.div>
            </div>

            {/* Animated divider line */}
            <motion.div
              className="relative mt-6 sm:mt-8 h-[1px] overflow-hidden"
              initial={{ width: 0, opacity: 0 }}
              animate={{ 
                width: phase === 'tagline' || phase === 'complete' ? 180 : 0,
                opacity: phase === 'tagline' || phase === 'complete' ? 1 : 0,
              }}
              transition={{ 
                duration: 0.8, 
                ease: [0.25, 0.46, 0.45, 0.94] as const,
              }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-primary to-transparent"
                animate={{
                  x: ["-100%", "100%"],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
            </motion.div>

            {/* Tagline */}
            <motion.div
              className="mt-6 sm:mt-8 flex items-center gap-2 sm:gap-4"
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: phase === 'tagline' || phase === 'complete' ? 1 : 0,
              }}
              transition={{ duration: 0.6 }}
            >
              {taglineWords.map((word, i) => (
                <motion.div
                  key={word}
                  className="flex items-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ 
                    opacity: phase === 'tagline' || phase === 'complete' ? 1 : 0,
                    y: phase === 'tagline' || phase === 'complete' ? 0 : 20,
                  }}
                  transition={{ 
                    duration: 0.6, 
                    delay: 0.1 + i * 0.12,
                    ease: [0.25, 0.46, 0.45, 0.94] as const,
                  }}
                >
                  <span className="text-xs sm:text-sm md:text-base tracking-[0.2em] sm:tracking-[0.3em] uppercase text-muted-foreground/80 font-medium">
                    {word}
                  </span>
                  {i < taglineWords.length - 1 && (
                    <motion.span 
                      className="ml-2 sm:ml-4 text-primary/60 text-xs"
                      initial={{ scale: 0 }}
                      animate={{ 
                        scale: phase === 'tagline' || phase === 'complete' ? 1 : 0,
                      }}
                      transition={{ 
                        duration: 0.3, 
                        delay: 0.3 + i * 0.12,
                        ease: "backOut",
                      }}
                    >
                      ◆
                    </motion.span>
                  )}
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Elegant corner accents */}
          <motion.div
            className="absolute top-6 left-6 sm:top-10 sm:left-10 w-12 h-12 sm:w-16 sm:h-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-primary/50 to-transparent" />
            <div className="absolute top-0 left-0 h-full w-[1px] bg-gradient-to-b from-primary/50 to-transparent" />
          </motion.div>
          
          <motion.div
            className="absolute bottom-6 right-6 sm:bottom-10 sm:right-10 w-12 h-12 sm:w-16 sm:h-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <div className="absolute bottom-0 right-0 w-full h-[1px] bg-gradient-to-l from-primary/50 to-transparent" />
            <div className="absolute bottom-0 right-0 h-full w-[1px] bg-gradient-to-t from-primary/50 to-transparent" />
          </motion.div>

          {/* Smooth horizontal scan line */}
          <motion.div
            className="absolute inset-x-0 h-[1px] pointer-events-none"
            style={{
              background: "linear-gradient(90deg, transparent 0%, hsl(var(--primary) / 0.15) 20%, hsl(var(--primary) / 0.3) 50%, hsl(var(--primary) / 0.15) 80%, transparent 100%)",
            }}
            initial={{ top: "-5%" }}
            animate={{ top: "105%" }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "linear",
              repeatDelay: 1,
            }}
          />

          {/* Vignette overlay for depth */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: "radial-gradient(ellipse at center, transparent 0%, transparent 50%, hsl(var(--background) / 0.4) 100%)",
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
